import type { PublicClientApplication } from '@azure/msal-browser';

/**
 * The application's MSAL instance, held where **anything** may read it — including code that
 * must never start the UI.
 *
 * 🔴 **What this module breaks (BL-146).** `services/conversionsApi.ts` used to read
 * `msalInstance` from `../main`, and `main.tsx` boots the app at module scope. That was not
 * merely an unwanted side effect, it was a **cycle**:
 *
 * ```
 * services/conversionsApi  →  main  →  App  →  pages/ClientPage  →  services/conversionsApi
 * ```
 *
 * Under vite-node an `import('./conversionsApi')` on that graph **never resolves** — the boot
 * test that pins this behaviour hung for 30 s before the split, rather than failing an
 * assertion. In the browser the cycle resolved but cost the same thing in a different currency:
 * importing the API layer *started the application*, so every DOM test in this repo mocks the
 * module wholesale, and the first symptom of forgetting was Testing Library reporting *"found
 * multiple elements"* on an assertion that was correct.
 *
 * ⚠ **The instance is INJECTED, never constructed here, and that is the load-bearing part.**
 * `new PublicClientApplication(...)` **throws** on malformed config — most cheaply when
 * `clientId` is `undefined`, which is what an unset `VITE_*` inlines to. At module scope that
 * throw escapes before any React root or error boundary exists: no UI, white page, console
 * trace only. That shipped on 2026-07-31 and white-screened every user. Construction stays
 * inside `main.tsx`'s try/catch; this module only ever *holds* what it is given.
 *
 * ⚠ **`import type`, deliberately** — a value import of `@azure/msal-browser` here would pull
 * the library into every consumer's graph and hand back a chunk of the weight the split exists
 * to remove.
 */
let instance: PublicClientApplication | undefined;

/** Called once by `main.tsx` after `new PublicClientApplication(...)` succeeds. */
export function setMsalInstance(value: PublicClientApplication): void {
  instance = value;
}

/**
 * The instance, or `undefined` before boot completes.
 *
 * Callers must handle `undefined` rather than assert it away: the honest failure is a named
 * one (`AuthRequiredError('not-initialized')`), not `cannot read properties of undefined`.
 */
export function getMsalInstance(): PublicClientApplication | undefined {
  return instance;
}
